import { KBType } from '@/modules/gpt-chats/models/kb';
import { aiService } from '@/modules/gpt-chats/services/ai.service';
import {
  IGetAgentsByFolderIdPayload,
  IGetAllKBPayload,
} from '@/modules/gpt-chats/types/kb.service.type';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parseAsArrayOf, parseAsString, parseAsInteger, useQueryStates } from 'nuqs';

export const useGetAllKB = (options: IGetAllKBPayload, enabled = true) => {
  return useQuery({
    queryKey: ['kb', options.kb_type || 'all', options],
    queryFn: () => aiService.kb.getAllKB(options),
    enabled,
  });
};

export const useAddQnA = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['kb', 'qna'],
    mutationFn: aiService.kb.addQnA,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb', KBType.QnA] });
      queryClient.invalidateQueries({ queryKey: ['kb', 'all'] });
    },
  });
};

export const useAddText = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['kb', 'text'],
    mutationFn: aiService.kb.addText,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb', KBType.TEXT] });
      queryClient.invalidateQueries({ queryKey: ['kb', 'all'] });
    },
  });
};

export const useAddLink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['kb', 'link'],
    mutationFn: aiService.kb.addLink,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb', KBType.LINK] });
      queryClient.invalidateQueries({ queryKey: ['kb', 'all'] });
    },
  });
};

export const useUpdateKB = (kbType: KBType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['kb', 'update', kbType],
    mutationFn: aiService.kb.updateKb,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kb', kbType] }),
  });
};

export const useAddFiles = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['kb', 'files'],
    mutationFn: aiService.kb.addfiles,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb', KBType.FILE] });
      queryClient.invalidateQueries({ queryKey: ['kb', 'all'] });
    },
  });
};

export const useDeleteKbById = (kbType: KBType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['kb', 'delete'],
    mutationFn: aiService.kb.deleteKbById,
    onSuccess: (res) => {
      if (res.is_success) {
        queryClient.invalidateQueries({ queryKey: ['kb', kbType] });
        queryClient.invalidateQueries({ queryKey: ['kb', 'all'] });
      }
    },
  });
};

export const useKBQueryParams = () => {
  const [queryParams, setQueryParams] = useQueryStates({
    search: parseAsString.withDefault(''),
    types: parseAsArrayOf(parseAsString).withDefault([]),
    page: parseAsInteger.withDefault(1),
    page_size: parseAsInteger.withDefault(10),
  });

  return { queryParams, setQueryParams };
};

export const useReembedKb = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['kb', 'reembed'],
    mutationFn: aiService.kb.reembedKb,
    onSuccess: (res, payload) => {
      if (res.is_success) {
        const types = new Set(payload.kbs.map((item) => item.type));
        types.forEach((type) => queryClient.invalidateQueries({ queryKey: ['kb', type] }));
      }
    },
  });
};

export const useCreateFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['kb', 'create', 'folder'],
    mutationFn: aiService.kb.createFolder,
    onSuccess: (res) => {
      if (res.success) {
        queryClient.invalidateQueries({ queryKey: ['kb', 'folders'] });
      }
    },
  });
};

export const useUpdateFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['kb', 'update', 'folder'],
    mutationFn: aiService.kb.updateFolder,
    onSuccess: (res) => {
      if (res.success) {
        queryClient.invalidateQueries({ queryKey: ['kb', 'folders'] });
      }
    },
  });
};

export const useGetFolderList = (enabled = true) => {
  return useQuery({
    queryKey: ['kb', 'folders'],
    queryFn: () => aiService.kb.getFolderList(),
    enabled,
  });
};

export const useDeleteFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['kb', 'delete', 'folder'],
    mutationFn: aiService.kb.deleteFolder,
    onSuccess: (res) => {
      if (res.success) {
        queryClient.invalidateQueries({ queryKey: ['kb', 'folders'] });
      }
    },
  });
};

export const useGetAgentsByFolderId = (payload: IGetAgentsByFolderIdPayload, enabled = true) => {
  return useQuery({
    queryKey: ['kb', 'agents', payload.folder_id],
    queryFn: () => aiService.kb.getAgentsByFolderId(payload),
    enabled,
  });
};
